import { ClipboardIcon, SparkleIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from 'components/button/Button';
import { Dropzone } from 'components/Dropzone';
import { Pill } from 'components/pill/Pill';
import { TextArea } from 'components/textarea/TextArea';
import { getDisplayError } from '@utils/get-display-error';

import { useAIDraftCtx } from './context';
import { triggerExtractLanes } from '../../models';
import { trackEvent } from '@utils/track-event';

function fileToBase64(file: globalThis.File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new globalThis.FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] ?? '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const ACCEPTED_TYPES =
  'application/pdf,image/png,image/jpeg,image/webp,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

export const UploadStep = () => {
  const { state, dispatch } = useAIDraftCtx();
  const [isPasting, setIsPasting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleExtract = async () => {
    if (state.files.length === 0) return;

    setIsSubmitting(true);

    try {
      const files = await Promise.all(
        state.files.map(async (file) => ({
          name: file.name,
          mimeType: file.type as any,
          base64: await fileToBase64(file),
        })),
      );

      const { draftId } = await triggerExtractLanes({
        name: state.draftName,
        files,
        extraContext: state.extraContext || undefined,
      });

      trackEvent('ai_draft_extraction_started', {
        draft_id: draftId,
        file_count: files.length,
        has_extra_context: Boolean(state.extraContext),
      });

      dispatch({ type: 'START_EXTRACTION', draftId });
    } catch (err: any) {
      setIsSubmitting(false);
      const message = getDisplayError(err);
      toast.error('Kon extractie niet starten: ' + message);
    }
  };

  const handlePasteFromClipboard = async () => {
    setIsPasting(true);
    try {
      const clipboardItems = await globalThis.navigator.clipboard.read();
      const newFiles: globalThis.File[] = [];

      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (type.startsWith('image/') || type === 'application/pdf') {
            const blob = await item.getType(type);
            const extension = type.split('/')[1] ?? 'png';
            const file = new globalThis.File([blob], `clipboard-${Date.now()}.${extension}`, { type });
            newFiles.push(file);
          }
        }
      }

      if (newFiles.length > 0) {
        dispatch({ type: 'SET_FILES', files: [...state.files, ...newFiles] });
        toast.success(`${newFiles.length} bestand(en) geplakt`);
      } else {
        toast.error('Geen afbeelding of PDF gevonden in klembord');
      }
    } catch (err: any) {
      toast.error('Kon niet plakken vanuit klembord');
    } finally {
      setIsPasting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <div className="mb-4">
          <Pill color="primary" size="md">
            Bestanden uploaden
          </Pill>
        </div>
        <h2 className="heading-one mb-2">Maak een offerte vanuit ruwe input</h2>
        <p className="text-fg-secondary">
          Drop screenshots, Excel-bestanden of PDF's — AI leest de bestemmingen, volumes en details uit en maakt een
          gestructureerde draft.
        </p>
      </div>

      <div className="mb-4">
        <Dropzone
          files={state.files}
          onUpload={(files) => dispatch({ type: 'SET_FILES', files })}
          accept={ACCEPTED_TYPES}
          allowMultiple={true}
        />
      </div>

      <div className="mb-6">
        <Button
          onTrigger={handlePasteFromClipboard}
          isDisabled={isPasting}
          iconLeft={<ClipboardIcon className="button-icon" />}
        >
          Plak uit klembord
        </Button>
      </div>

      <div className="mb-6">
        <label className="label-text block mb-1.5">Extra context voor AI</label>
        <TextArea
          value={state.extraContext}
          onChange={(val) => dispatch({ type: 'SET_EXTRA_CONTEXT', extraContext: val })}
          placeholder="Alles te laden in Antwerpen. Enkel tautliners. Richtprijzen per rit."
          rows={3}
          spellCheck={true}
        />
        <p className="text-sm text-fg-muted mt-1">
          Geef extra info mee die niet in de bestanden staat — dit helpt AI nauwkeuriger te extraheren.
        </p>
      </div>

      <div className="flex justify-center">
        <Button
          color="primary"
          onTrigger={handleExtract}
          isDisabled={state.files.length === 0 || isSubmitting}
          isLoading={isSubmitting}
          iconLeft={<SparkleIcon className="button-icon" />}
        >
          Start extractie
        </Button>
      </div>
    </div>
  );
};
